// Documentation + sample data for the program import format.
// Kept in one place so the import modal, the sample download and any docs
// stay in sync. The authoritative parser lives in backend/workout-import.js.

export const IMPORT_FORMAT_VERSION = 1;

export const EXERCISE_TYPES = [
  { value: 'weight_reps', label: 'Weight & reps', hint: 'Sets of reps, optionally loaded (bench press, squats)' },
  { value: 'weight_time', label: 'Weight & time', hint: 'Sets held for time (planks, carries)' },
  { value: 'cardio_basic', label: 'Cardio', hint: 'One distance/duration effort (easy run, bike)' },
  { value: 'cardio_segments', label: 'Cardio intervals', hint: 'Multiple segments (warmup / intervals / cooldown)' },
  { value: 'stretch', label: 'Stretch / mobility', hint: 'Any of duration, reps, weight or band - all optional' }
];

export const WEIGHT_UNITS = ['lbs', 'kg'];
export const DISTANCE_UNITS = ['miles', 'km', 'meters', 'yards'];

// Field reference rendered as tables in the format guide.
export const FORMAT_SECTIONS = [
  {
    title: 'Top level',
    description: 'The document is a single JSON object.',
    fields: [
      { name: 'version', type: 'number', required: false, description: `Format version. Currently ${IMPORT_FORMAT_VERSION}.` },
      { name: 'program', type: 'object', required: true, description: 'The program itself (see below).' },
      { name: 'templates', type: 'array', required: false, description: 'Reusable workout definitions that workouts reference by name or "key". Use these when the same session repeats across weeks.' },
      { name: 'workouts', type: 'array', required: true, description: 'Every session in the program, in order.' }
    ]
  },
  {
    title: 'program',
    fields: [
      { name: 'name', type: 'string', required: true, description: 'Program name.' },
      { name: 'description', type: 'string', required: false, description: 'Short description shown in the program list.' },
      { name: 'notes', type: 'string', required: false, description: 'Longer notes for the whole program.' }
    ]
  },
  {
    title: 'templates[]  (a workout definition)',
    description: 'Also used inline when a workout defines its own "items".',
    fields: [
      { name: 'key', type: 'string', required: false, description: 'Short id workouts use to reference this template. Defaults to "name".' },
      { name: 'name', type: 'string', required: true, description: 'Workout name, e.g. "Upper A" or "Long Run".' },
      { name: 'description', type: 'string', required: false, description: 'Optional description.' },
      { name: 'notes', type: 'string', required: false, description: 'Notes shown when the workout is logged.' },
      { name: 'items', type: 'array', required: true, description: 'Ordered exercises, supersets and rest blocks.' }
    ]
  },
  {
    title: 'workouts[]  (a session in the program)',
    fields: [
      { name: 'phase', type: 'number', required: false, description: 'Phase/block number.' },
      { name: 'week', type: 'number', required: false, description: 'Week number.' },
      { name: 'day', type: 'number', required: false, description: 'Day number within the week.' },
      { name: 'name', type: 'string', required: false, description: 'Display name. Generated from phase/week/day + template name if omitted.' },
      { name: 'sort_order', type: 'number', required: false, description: 'Ordering. Defaults to the position in the array.' },
      { name: 'notes', type: 'string', required: false, description: 'Notes for this session only.' },
      { name: 'template', type: 'string', required: 'one of', description: 'Name or "key" of an entry in "templates".' },
      { name: 'items', type: 'array', required: 'one of', description: 'Define the workout inline instead of referencing a template. Pair with "template_name" to name it.' },
      { name: 'cardio_overrides', type: 'object', required: false, description: 'Per-session cardio changes on a shared template, keyed by exercise name: {"Long Run": {"distance": 12}}. Use "segments" instead for interval exercises.' }
    ]
  },
  {
    title: 'items[]  — exercise',
    description: 'The default item type; "type": "exercise" is optional.',
    fields: [
      { name: 'exercise', type: 'string | object', required: true, description: 'Exercise name, or {"name", "exercise_type", "category", "notes"} to describe a new one.' },
      { name: 'exercise_type', type: 'string', required: false, description: 'weight_reps | weight_time | cardio_basic | cardio_segments | stretch. Inferred from the data you provide if omitted - stretch is never inferred, so state it explicitly.' },
      { name: 'notes', type: 'string', required: false, description: 'Coaching notes for this exercise in this workout.' },
      { name: 'sets', type: 'array | number', required: false, description: 'Array of set objects, or a count used with the item-level "reps"/"weight" shorthand.' },
      { name: 'cardio', type: 'object', required: false, description: '{"distance", "distance_unit", "duration", "notes"} for a single cardio effort.' },
      { name: 'segments', type: 'array', required: false, description: 'Interval segments: [{"distance", "distance_unit", "duration", "notes"}].' },
      { name: 'superset_group', type: 'string | number', required: false, description: 'Alternative to a "superset" item: give consecutive exercises the same value to group them.' }
    ]
  },
  {
    title: 'items[]  — sets[]',
    fields: [
      { name: 'set', type: 'number', required: false, description: 'Set number. Defaults to its position.' },
      { name: 'weight', type: 'number', required: false, description: 'Prescribed weight.' },
      { name: 'weight_unit', type: 'string', required: false, description: `${WEIGHT_UNITS.join(' | ')}. Defaults to lbs.` },
      { name: 'reps', type: 'number | string', required: false, description: 'A number ("8") or a prescription ("8-10", "AMRAP", "8 each side").' },
      { name: 'duration', type: 'seconds | "mm:ss"', required: false, description: 'For time-based sets (planks, carries).' },
      { name: 'resistance', type: 'string', required: false, description: 'Free-text resistance for stretches, e.g. "red band". Sits alongside weight, not instead of it.' },
      { name: 'notes', type: 'string', required: false, description: 'Notes for this set.' }
    ]
  },
  {
    title: 'items[]  — superset',
    fields: [
      { name: 'type', type: '"superset"', required: true, description: 'Marks a superset group.' },
      { name: 'exercises', type: 'array', required: true, description: 'Two or more exercise items performed back to back.' },
      { name: 'notes', type: 'string', required: false, description: 'Applied to each exercise that has no notes of its own.' }
    ]
  },
  {
    title: 'items[]  — rest',
    fields: [
      { name: 'type', type: '"rest"', required: true, description: 'A rest block between exercises.' },
      { name: 'rest_duration_seconds', type: 'seconds | "mm:ss"', required: true, description: 'How long to rest.' },
      { name: 'notes', type: 'string', required: false, description: 'Optional note.' }
    ]
  }
];

export const FORMAT_NOTES = [
  'Durations accept seconds (90), clock strings ("1:30", "1:05:00") or unit values ("15 min").',
  'Distances accept miles, km, meters or yards - defaults to miles.',
  'Exercises are matched to your library by name, ignoring case and punctuation ("Pull-Up" matches "pull up"). Anything unmatched is listed for you to map or create before the import runs.',
  'Every workout becomes a program-scoped template, so editing one week never changes another.',
  'Nothing is written until you press Import - validation and matching are a dry run.'
];

export const SAMPLE_PROGRAM = {
  version: IMPORT_FORMAT_VERSION,
  program: {
    name: '8 Week Strength + Base',
    description: 'Three lifting days and two runs per week',
    notes: 'Add 5 lb to the main lift each week if all sets are completed.'
  },
  templates: [
    {
      key: 'upper-a',
      name: 'Upper A',
      notes: 'Leave 1-2 reps in reserve on every working set.',
      items: [
        {
          exercise: 'Barbell Bench Press',
          notes: 'Main lift',
          sets: [
            { set: 1, weight: 135, weight_unit: 'lbs', reps: 8, notes: 'Warmup' },
            { set: 2, weight: 155, reps: '6-8' },
            { set: 3, weight: 155, reps: '6-8' }
          ]
        },
        {
          type: 'superset',
          notes: 'Back to back, 90s rest after the pair',
          exercises: [
            { exercise: 'Pull Up', sets: 3, reps: 'AMRAP' },
            { exercise: 'Dumbbell Curl', sets: 3, reps: 12, weight: 30 }
          ]
        },
        { type: 'rest', rest_duration_seconds: '2:00', notes: 'Before core work' },
        {
          exercise: { name: 'Plank', exercise_type: 'weight_time', category: 'core' },
          sets: [
            { set: 1, duration: '1:00' },
            { set: 2, duration: '1:00' }
          ]
        }
      ]
    },
    {
      key: 'long-run',
      name: 'Long Run',
      items: [
        {
          exercise: { name: 'Long Run', exercise_type: 'cardio_basic', category: 'cardio' },
          notes: 'Conversational pace',
          cardio: { distance: 8, distance_unit: 'miles', duration: '1:12:00' }
        }
      ]
    },
    {
      key: 'intervals',
      name: 'Track Intervals',
      items: [
        {
          exercise: { name: 'Track Intervals', exercise_type: 'cardio_segments', category: 'cardio' },
          segments: [
            { segment: 1, distance: 1, distance_unit: 'miles', duration: '9:00', notes: 'Warmup' },
            { segment: 2, distance: 400, distance_unit: 'meters', duration: '1:32', notes: 'Interval' },
            { segment: 3, distance: 400, distance_unit: 'meters', duration: '2:30', notes: 'Recovery jog' },
            { segment: 4, distance: 1, distance_unit: 'miles', duration: '9:30', notes: 'Cooldown' }
          ]
        }
      ]
    }
  ],
  workouts: [
    { week: 1, day: 1, template: 'upper-a' },
    { week: 1, day: 3, template: 'intervals', name: 'Week 1 Day 3 - Speed' },
    { week: 1, day: 5, template: 'long-run' },
    { week: 2, day: 1, template: 'upper-a', notes: 'Add 5 lb to bench' },
    { week: 2, day: 3, template: 'intervals' },
    {
      week: 2,
      day: 5,
      template: 'long-run',
      cardio_overrides: {
        'Long Run': { distance: 10, distance_unit: 'miles', duration: '1:30:00' }
      }
    }
  ]
};

export const SAMPLE_PROGRAM_JSON = JSON.stringify(SAMPLE_PROGRAM, null, 2);

// Minimal skeleton for someone starting from scratch.
export const STARTER_PROGRAM_JSON = JSON.stringify(
  {
    version: IMPORT_FORMAT_VERSION,
    program: { name: 'My Program', description: '', notes: '' },
    templates: [
      {
        key: 'day-1',
        name: 'Day 1',
        items: [
          { exercise: 'Barbell Squat', sets: [{ set: 1, weight: 185, reps: 5 }] },
          { type: 'rest', rest_duration_seconds: '2:00' }
        ]
      }
    ],
    workouts: [{ week: 1, day: 1, template: 'day-1' }]
  },
  null,
  2
);

export function downloadJson(filename, json) {
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
